/**
 * Minimum-necessary reveal gate — shows a masked value by default, requires
 * the staff user to enter a reason before the clear value is fetched +
 * audited as `phi_reveal`.
 *
 * Props:
 *   patientId    string   — user._id of the target patient
 *   field        string   — "dob" | "ssn" | "full_address" | "phone"
 *   maskedValue  string   — what to show by default (e.g. "••/••/19••")
 *   icon         node     — optional icon slot
 */
import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Eye, EyeOff, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function RevealPhi({ patientId, field, maskedValue, icon = null, className = "" }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);

  const onRequest = async () => {
    if (reason.trim().length < 3) {
      toast.error("Please enter a brief reason (e.g. 'verifying identity for refill').");
      return;
    }
    setLoading(true);
    try {
      const { data } = await API.post("/phi/reveal", {
        patient_id: patientId,
        field,
        reason: reason.trim(),
      });
      setValue(data?.value || "—");
      setOpen(false);
      setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reveal value");
    } finally {
      setLoading(false);
    }
  };

  const hide = () => setValue("");

  return (
    <>
      <span className={`inline-flex items-center gap-1 ${className}`} data-testid={`reveal-${field}`}>
        {icon}
        <span className="text-slate-700">
          {value ? (
            <strong className="text-slate-900" data-testid={`reveal-${field}-value`}>{value || "—"}</strong>
          ) : (
            <span className="tracking-wider">{maskedValue || "••••"}</span>
          )}
        </span>
        {value ? (
          <button
            type="button"
            onClick={hide}
            className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-700 ml-1 inline-flex items-center gap-0.5"
            data-testid={`reveal-${field}-hide`}
          >
            <EyeOff className="w-3 h-3" /> hide
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-[10px] uppercase tracking-wider text-purple-700 hover:text-purple-900 font-semibold ml-1 inline-flex items-center gap-0.5"
            data-testid={`reveal-${field}-btn`}
          >
            <Eye className="w-3 h-3" /> reveal
          </button>
        )}
      </span>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setReason(""); }}>
        <DialogContent className="max-w-md" data-testid="reveal-phi-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Reveal protected health information
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600 leading-relaxed">
              You&apos;re about to reveal the patient&apos;s <strong>{field.replace("_", " ")}</strong>.
              HIPAA requires a documented business reason for access — it will be logged
              in your audit trail and is visible to the patient on their &quot;Who accessed
              my record?&quot; page.
            </p>
            <div>
              <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Reason <span className="text-rose-500">*</span>
              </Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 200))}
                placeholder="e.g. Verifying identity for refill approval"
                className="mt-1"
                data-testid="reveal-reason-input"
                autoFocus
              />
              <p className="text-[10px] text-slate-400 mt-1">{reason.length}/200</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={loading}>Cancel</Button>
            <Button
              onClick={onRequest}
              disabled={loading || reason.trim().length < 3}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="reveal-confirm-btn"
            >
              {loading ? "Revealing…" : "Reveal + log reason"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
